/**
 * Retrieves size and category information for a specific size grid code.
 *
 * @param {string} sizeGridCode - A unique identifier for a specific size grid.
 * @returns {Promise<Object>} An object containing size grid data, product category, and countries.
 *
 * @property {Ref<any>} sizeGridData - Detailed information about the size grid.
 * @property {ComputedRef<Object | null>} productCategory - The category associated with the size grid.
 * @property {Array<Object>} countries - List of countries with size information.
 *
 * @description
 * This composable provides comprehensive size and category information:
 * - Retrieves size grid data based on a specific code
 * - Determines the product category for a given size
 * - Supports dynamic size and category lookup
 *
 * @example
 * // Retrieve size and category information
 * const { sizeGridData, productCategory, countries } = await useFitFinder('M')
 *
 * @remarks
 * - Requires active Nuxt context
 * - Depends on useSizeRetrieveData for data retrieval
 * - Supports complex category and size hierarchy
 * - Handles cases with missing or undefined data
 */
export const useFitFinder = async (sizeGridCode: string) => {
  const nuxtApp = useNuxtApp()

  const { getSizesByCode, countries, macroCategories } =
    await useSizeRetrieveData()

  const { data: sizeGridData } = await nuxtApp.runWithContext(() =>
    getSizesByCode(sizeGridCode)
  )

  const productCategory = computed(() => {
    if (!sizeGridCode) return null
    if (!macroCategories) return null

    for (const category of macroCategories) {
      const subCategories = category?.subCategories
      if (!subCategories) continue
      for (const subCategory of subCategories) {
        const sizes = subCategory?.sizes
        if (!sizes) continue
        for (const size of sizes) {
          if (size === sizeGridCode) {
            return subCategory
          }
        }
      }
    }
    return null
  })

  return {
    sizeGridData,
    productCategory,
    countries,
  }
}
